/**
 * Sprint S-2 · #25 · Help Center `/ayuda`.
 *
 * 10 artículos curados que cubren los flujos más preguntados. Página pública
 * (no requiere login) — SEO friendly y útil para soporte. Search local en JS
 * (no necesita backend).
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import GlobalHeader from "../components/layout/GlobalHeader";
import { useStaticSeo } from "../lib/seo";
import {
  MagnifyingGlass, Question, ArrowRight, Storefront, Wrench, Calendar,
  Receipt, CurrencyEur, Sparkle, ShieldCheck, Bell, UsersThree, Lock,
} from "@phosphor-icons/react";

const ARTICLES = [
  {
    id: "catalogo",
    icon: Storefront,
    title: "Cómo añadir y editar servicios en mi catálogo",
    summary: "Configura nombres, precios, duración, descripciones y sectores en menos de 5 minutos.",
    tags: ["catálogo", "servicios", "precios"],
    body: [
      "Ve a **Catálogo** en el menú principal (o `/catalog`).",
      "Pulsa **\"Añadir servicio\"** y completa: nombre, descripción corta, precio, duración estimada y sector.",
      "Si el servicio requiere validación manual (ej: pintura, peritaje), marca **\"Requiere validación\"** — el cliente no recibirá confirmación automática.",
      "Activa/desactiva servicios con el toggle sin borrarlos.",
      "Los servicios marcados con precio variable usan **`price_min` / `price_max`** y mostrarán \"desde X €\" en la ficha pública.",
    ],
  },
  {
    id: "reservas",
    icon: Calendar,
    title: "Cómo gestionar reservas online y pendientes",
    summary: "Confirma, edita o cancela reservas. Configura la validación manual por sector.",
    tags: ["reservas", "agenda"],
    body: [
      "Las reservas entrantes aparecen en **Agenda** y en el Dashboard como contador.",
      "Si una reserva tiene `pending_review`, debes confirmarla manualmente desde el detalle.",
      "Activa o desactiva validación manual por servicio en su tarjeta de Catálogo.",
      "Si necesitas reasignar a otro técnico, edita la reserva y cambia el campo \"operador\".",
    ],
  },
  {
    id: "facturas",
    icon: Receipt,
    title: "Facturas con numeración correlativa (AG-YYYY-XXXX)",
    summary: "Tu serie es única, no se rompe entre meses y se exporta a PDF en un click.",
    tags: ["facturas", "fiscal"],
    body: [
      "Cuando marcas un servicio como **Pagado**, AutoGlow genera una factura con numeración correlativa (`AG-2026-0042`).",
      "Para exportar el PDF, abre el detalle del servicio → **\"Descargar factura\"**.",
      "Si necesitas anular, usa **\"Marcar como anulada\"** — la numeración se mantiene y la factura aparece tachada.",
      "Histórico completo desde `/expenses` filtro \"Solo facturas\".",
    ],
  },
  {
    id: "impuestos",
    icon: CurrencyEur,
    title: "Modelos AEAT calculados automáticamente",
    summary: "303, 111, 115, 130, 180, 190, 390 — todos calculados desde tus servicios y gastos.",
    tags: ["impuestos", "aeat", "303"],
    body: [
      "Entra en **/tax**. Verás cada modelo en su tarjeta con el cálculo del trimestre actual.",
      "**303 (IVA trimestral)**: agrega base imponible + cuota repercutida/soportada de tus servicios y gastos.",
      "**111** (IRPF profesionales) requiere que marques el gasto con `retention_type=profesional` (15%).",
      "**115** (IRPF alquileres) usa `retention_type=alquiler_urbano` (19%).",
      "**130** (pago fraccionado autónomos): se calcula automáticamente con tus ingresos/gastos.",
      "Puedes **enviar a tu gestor** un email con CSV + PDF de cualquier modelo desde el botón \"Enviar a gestor\".",
    ],
  },
  {
    id: "marketplace",
    icon: Sparkle,
    title: "Activar mi centro en el marketplace público",
    summary: "Aparecer en /marketplace y ser indexable por Google.",
    tags: ["marketplace", "seo", "público"],
    body: [
      "Ve a **Ajustes → Perfil público** y activa **\"Hacer visible mi centro\"**.",
      "Completa: nombre, logo, foto de cabecera (1920×800px), descripción (mín. 80 palabras), dirección, teléfono.",
      "Configura **horarios por día** en Ajustes — necesarios para que aparezcas como \"Disponible hoy\" y para el JSON-LD SEO.",
      "Asegúrate de tener al menos **3 servicios en catálogo** para que la ficha tenga contenido suficiente.",
      "Una vez activado, tu URL pública será `https://ag-ecosystem.com/centro/<tu-slug>`.",
    ],
  },
  {
    id: "ia",
    icon: Sparkle,
    title: "Asistente IA en tu ficha pública",
    summary: "El chat conoce tu catálogo y puede iniciar reservas por el cliente.",
    tags: ["ia", "chat", "reservas"],
    body: [
      "Tu ficha pública incluye un asistente IA que conoce tu catálogo, precios y horarios.",
      "Cuando un cliente expresa intención de reservar (\"quiero el detailing completo el viernes a las 10\"), el chat le ofrece un CTA que pre-selecciona el servicio y la fecha.",
      "Mantén tu catálogo actualizado para que el asistente dé información precisa.",
    ],
  },
  {
    id: "equipo",
    icon: UsersThree,
    title: "Invitar a mi equipo (gestor, colaborador)",
    summary: "Roles con permisos diferentes para tu fiscal o tus técnicos.",
    tags: ["equipo", "roles", "permisos"],
    body: [
      "En **Ajustes → Equipo** envía una invitación por email indicando el rol.",
      "**Admin**: control total (suyo + facturación + fiscal).",
      "**Gestor**: solo accede a fiscal/contable. Perfecto para tu gestoría.",
      "**Colaborador**: solo ve agenda, completa servicios, no toca configuración ni precios.",
    ],
  },
  {
    id: "notificaciones",
    icon: Bell,
    title: "Notificaciones push y recordatorios",
    summary: "Configura recordatorios al cliente y push al móvil del taller.",
    tags: ["notificaciones", "push", "recordatorios"],
    body: [
      "AutoGlow es una PWA: en tu móvil, pulsa **\"Añadir a pantalla de inicio\"** desde Chrome/Safari.",
      "Una vez instalada, en Ajustes activa **Notificaciones push** para recibir alertas de nuevas reservas.",
      "Los recordatorios al cliente (email 24h antes) se configuran por defecto. Puedes desactivarlos en Ajustes → Notificaciones.",
    ],
  },
  {
    id: "seguridad",
    icon: ShieldCheck,
    title: "Pasarela WebAuthn / Passkeys",
    summary: "Login sin contraseñas con Face ID, Touch ID, Windows Hello o llaves físicas.",
    tags: ["seguridad", "passkey", "webauthn"],
    body: [
      "Ve a **Ajustes → Seguridad → Pasarelas / Passkeys**.",
      "Pulsa **\"Registrar nueva passkey\"** y sigue las indicaciones del navegador (Face ID, huella, Windows Hello, llave YubiKey, etc.).",
      "Una vez registrada, en el login podrás entrar sin escribir contraseña (Conditional UI — el navegador detecta tus passkeys automáticamente).",
      "Puedes registrar varias passkeys (móvil, ordenador, llave física) para tener backups.",
    ],
  },
  {
    id: "soporte",
    icon: Lock,
    title: "Cancelar suscripción / Cambiar plan",
    summary: "Upgrade, downgrade o cancelación. Sin permanencias.",
    tags: ["billing", "plan", "cancelar"],
    body: [
      "En **Ajustes → Plan y facturación** verás tu plan actual y opciones de upgrade.",
      "Para cancelar, pulsa **\"Gestionar suscripción\"** — te llevará al portal seguro de Stripe.",
      "Los planes Lifetime no se renuevan ni cancelan (son únicos vitalicios).",
      "Para dudas de facturación, escribe a soporte indicando tu NIF o slug del centro.",
    ],
  },
];

function ArticleCard({ a, q }) {
  const Icon = a.icon || Question;
  const highlight = q.trim().toLowerCase();
  const match = (str) => {
    if (!highlight) return str;
    const re = new RegExp(`(${highlight.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&")})`, "ig");
    return str.split(re).map((p, i) =>
      p.toLowerCase() === highlight
        ? <mark key={i} className="bg-amber-200 px-0.5">{p}</mark>
        : <span key={i}>{p}</span>
    );
  };
  return (
    <details className="group border border-gray-200 rounded-md bg-white open:shadow-md transition-shadow" data-testid={`help-article-${a.id}`}>
      <summary className="list-none cursor-pointer p-4 flex items-start gap-3 hover:bg-gray-50">
        <div className="w-10 h-10 rounded bg-[var(--ag-gold)] text-white grid place-items-center shrink-0">
          <Icon size={20} weight="duotone" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-sm">{match(a.title)}</div>
          <div className="text-xs text-gray-500 mt-1">{match(a.summary)}</div>
        </div>
        <ArrowRight size={16} className="text-[var(--ag-text-muted)] mt-3 group-open:rotate-90 transition-transform" />
      </summary>
      <div className="px-4 pb-4 pl-[68px] space-y-2 text-sm text-gray-700">
        {a.body.map((line, i) => (
          <p key={i} dangerouslySetInnerHTML={{
            __html: line.replace(/\*\*(.+?)\*\*/g, "<b>$1</b>").replace(/`([^`]+)`/g, "<code class='bg-gray-100 px-1 rounded font-mono text-xs'>$1</code>"),
          }} />
        ))}
      </div>
    </details>
  );
}

export default function Help() {
  useStaticSeo({
    title: "Centro de ayuda · AutoGlow Ecosystem",
    description: "Guías rápidas: catálogo, reservas, modelos AEAT, marketplace público, IA, equipo y más.",
    path: "/ayuda",
  });
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return ARTICLES;
    return ARTICLES.filter((a) =>
      a.title.toLowerCase().includes(term) ||
      a.summary.toLowerCase().includes(term) ||
      a.tags.some((t) => t.includes(term)) ||
      a.body.some((line) => line.toLowerCase().includes(term))
    );
  }, [q]);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="help-page">
      <GlobalHeader />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <div className="text-[11px] uppercase tracking-[0.25em] text-[var(--ag-gold)] mb-2">Centro de ayuda</div>
        <h1 className="font-display font-black text-3xl sm:text-5xl tracking-tighter">¿En qué te ayudamos?</h1>
        <p className="text-gray-600 mt-3">10 guías rápidas con todo lo que necesitas para sacar el máximo a AutoGlow.</p>

        <div className="relative mt-6">
          <MagnifyingGlass size={18} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ag-text-muted)]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar… (ej: 303, marketplace, passkey)"
            className="w-full pl-10 pr-3 py-3 rounded-md border border-gray-300 bg-white focus:outline-none focus:border-[var(--ag-gold)] text-sm"
            data-testid="help-search"
          />
        </div>

        <div className="mt-6 space-y-3" data-testid="help-articles">
          {filtered.length === 0 && (
            <div className="text-center py-12 text-gray-500 text-sm" data-testid="help-empty">
              No hay artículos para "<b>{q}</b>". <button onClick={() => setQ("")} className="text-[#0077B6] underline">Limpiar búsqueda</button>.
            </div>
          )}
          {filtered.map((a) => <ArticleCard key={a.id} a={a} q={q} />)}
        </div>

        <div className="mt-12 border-t border-gray-200 pt-8 text-center">
          <p className="text-sm text-gray-600">¿No encuentras lo que buscas?</p>
          <a href="mailto:soporte@ag-ecosystem.com" className="inline-flex items-center gap-2 mt-3 bg-[var(--ag-gold)] hover:bg-[#FFCD3C] text-white px-5 py-3 rounded text-sm font-semibold transition-colors" data-testid="help-contact">
            <PaperContactIcon /> Escribir a soporte
          </a>
        </div>
      </main>
    </div>
  );
}

function PaperContactIcon() {
  return <Question size={14} weight="fill" />;
}
